import { useEffect, useRef, useState } from 'react';
import useClickOutside from '@/hooks/useClickOutside';
import { twMerge } from 'tailwind-merge';
import { ChevronDownRegularGlyph } from '@acast-tech/decibel-glyphs';
import { ComboboxTag, ComboboxListItem, type Option, useHandleInputChange, useHandleInputKeyDown, useHandleOptionClick, useHandleRemoveOption } from '.';

type ComboboxProps = {
  options: Option[];
  onSelect: (options: Option[]) => void;
  onOpen?: () => void;
  defaultPlaceholder?: string;
  placeholder?: string;
  className?: string;
  width?: {
    mobile?: string;
    tablet?: string;
    desktop?: string;
  };
};

const Combobox = ({
  options,
  onSelect,
  onOpen,
  placeholder,
  defaultPlaceholder,
  className,
  width = {
    tablet: '100px',
    desktop: '200px',
  },
}: ComboboxProps) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);

  const { handleInputChange } = useHandleInputChange(options, setInputValue, setFilteredOptions, setIsOpen, selectedOptions);
  const { handleOptionClick } = useHandleOptionClick(selectedOptions, setSelectedOptions, setInputValue, onSelect);
  const { handleRemoveOption } = useHandleRemoveOption(selectedOptions, setSelectedOptions, onSelect);
  const { handleInputKeyDown } = useHandleInputKeyDown(inputValue, selectedOptions, handleRemoveOption, setIsOpen, setHighlightedIndex, highlightedIndex, filteredOptions, handleOptionClick, isOpen);

  const wrapperRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false));
  const optionsRef = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions, isOpen]);

  useEffect(() => {
    if (isOpen && optionsRef.current[highlightedIndex]) {
      optionsRef.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex, isOpen, filteredOptions]);

  return (
    <div
      className={twMerge(
        `relative w-full h-10`,
        width.desktop && `lg:w-[${width.desktop}]  lg:max-w-[${width.desktop}]`,
        width.mobile && `w-[${width.mobile}]`,
        width.tablet && `md:w-[${width.tablet}]`,
        className
      )}
    >
      <div
        ref={wrapperRef}
        tabIndex={0}
        className={twMerge(
          `absolute top-0 left-0 right-0 z-sticky rounded-[4px] p-2.5 border hover:border-primary h-max bg-white`,
          isFocused && 'shadow-[0_0_0_4px_rgba(64,64,168,0.6)]'
        )}
        onFocus={() => {
          setIsFocused(true);
          setIsOpen(true);

          
          onOpen?.();
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
      >
        {/* Selected options as chips/tags */}
        {selectedOptions.length > 0 && isOpen && (
          <div className="flex flex-wrap gap-1">
            {selectedOptions.map((option) => (
              <ComboboxTag key={option.id} option={option} onRemove={handleRemoveOption} />
            ))}
          </div>
        )}

        <input
          name="combobox-input"
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls="combobox-list"
          aria-activedescendant={
            isOpen && filteredOptions.length > 0
              ? `combobox-option-${highlightedIndex}`
              : undefined
          }
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          placeholder={
            isOpen && selectedOptions.length > 0
              ? ''
              : selectedOptions.length > 0
              ? placeholder
              : defaultPlaceholder
          }
          onBlur={() => setIsFocused(false)}
          className={twMerge(
            'w-full focus:outline-none placeholder-black',
            isOpen && 'placeholder-black/40',
            selectedOptions.length > 0 && isOpen && 'mt-2'
          )}
        />

        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {selectedOptions.length === 0 && (
            <ChevronDownRegularGlyph
              className={twMerge(
                'size-3 transition-transform duration-300 text-black/75',
                isOpen && 'rotate-180'
              )}
            />
          )}
          {selectedOptions.length > 0 && !isOpen && (
            <span className="text-xs text-chartColors-color1 font-medium bg-chartColors-color6/50 size-5 flex items-center justify-center rounded-sm">
              {selectedOptions.length}
            </span>
          )}
        </div>

        {isOpen && filteredOptions.length > 0 && (
          <ul
            id="combobox-list"
            role="listbox"
            className="absolute top-[103%] left-0 right-0 border bg-white rounded-sm list-none m-0 p-0 z-header text-sm shadow-comboboxList max-h-60 overflow-y-auto"
          >
            {filteredOptions.map((option, index) => (
              <ComboboxListItem
                key={option.id}
                option={option}
                onClick={handleOptionClick}
                ref={(el) => (optionsRef.current[index] = el)}
                index={index}
                state={{ selectedOptions, highlightedIndex, inputValue }}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Combobox;
